import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { shapes } from '@unite-us/client-utils';
import { Spinner } from 'common/spinners';
import { DynamicTable } from 'common/tables';
import { UnauthorizedMessage } from 'common/tables/DynamicTable/components';
import { Popover } from '@unite-us/app-components';
import firstLine from 'common/utils/firstLine';
import CaseRowIcon from 'src/components/Dashboard/components/Cases/CaseRowIcon';
import _ from 'lodash';
import { showFacesheetCasesTableDescription } from 'common/utils/FeatureFlags/flags';
import {
  PENDING_AUTHORIZATION_CASE_ACCESS_MESSAGE,
  PENDING_AUTHORIZATION_CASE_ACCESS_TIMEOUT_MS,
  shouldBlockPendingAuthorizationCaseAccess,
} from 'src/components/Cases/utils/pendingAuthorizationAccess';
import {
  clientNameColumn,
  serviceTypeColumn,
} from '../../utils/columnConfigs/baseColumns';
import FilterBar from '../FilterBar';

export function OpenCasesIndex(props) {
  const {
    paging,
    data,
    onNextClick,
    onPrevClick,
    isFetching,
    onCellClick,
    initialSort,
    groupId,
    isCC,
    filters,
    onFiltersChange,
    isCaseMgmtProvider,
    isCaseManager,
    fetchGroupsUsers,
    showDescription,
    sortConfig,
    setSort,
  } = props;

  const [pendingAuthPopoverCaseId, setPendingAuthPopoverCaseId] = useState(null);

  useEffect(() => {
    if (!pendingAuthPopoverCaseId) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setPendingAuthPopoverCaseId(null);
    }, PENDING_AUTHORIZATION_CASE_ACCESS_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [pendingAuthPopoverCaseId]);

  const onSortColumn = (column, sortDirection) => {
    if (sortConfig) {
      sortConfig(column, sortDirection);
    }
  };

  const getColumns = () => {
    const descColumn = {
      label: 'Description',
      formatData: (item) => firstLine(item.description),
      className: 'truncate',
    };

    return _.compact([
      clientNameColumn(setSort, onSortColumn),
      showDescription ? descColumn : null,
      serviceTypeColumn(),
      {
        label: 'Date Opened',
        sortColumn: 'opened_date',
        sortBy: setSort?.sidx,
        sortDirection: setSort?.sord,
        setSort: ({ sortBy, sortDirection }) => onSortColumn(sortBy, sortDirection),
        type: 'date',
      },
      {
        label: 'Last Updated',
        sortColumn: 'updated_at',
        sortBy: setSort?.sidx,
        sortDirection: setSort?.sord,
        setSort: ({ sortBy, sortDirection }) => onSortColumn(sortBy, sortDirection),
        type: 'date',
      },
    ]);
  };

  const isCasePendingAuth = (serviceCase) => (
    shouldBlockPendingAuthorizationCaseAccess({
      serviceCase,
      currentProviderId: groupId,
      isCaseManager,
    })
  );

  const togglePendingAuthPopover = (serviceCase) => {
    if (!isCasePendingAuth(serviceCase)) {
      setPendingAuthPopoverCaseId(null);
      return false;
    }

    const caseId = serviceCase?.id;

    setPendingAuthPopoverCaseId((current) => (
      current === caseId ? null : caseId
    ));

    return true;
  };

  const handleCellClick = (row, column) => {
    const serviceCase = data?.[row];

    if (togglePendingAuthPopover(serviceCase)) {
      return;
    }

    onCellClick(row, column);
  };

  return (
    <div>
      <h1 className="sr-only" id="dashboard-main-content-heading">Internal Cases - Open</h1>
      <FilterBar
        filters={filters}
        onFiltersChange={onFiltersChange}
        paging={paging}
        onNextClick={onNextClick}
        onPrevClick={onPrevClick}
        isFetching={isFetching && !_.isEmpty(data)}
        asyncSearch={fetchGroupsUsers}
      />
      <div className="dashboard-inner-content">
        {
          (isFetching && _.isEmpty(data)) ?
            <Spinner /> : (
              <DynamicTable
                id="open-cases-table"
                dataSet={data}
                onCellClick={handleCellClick}
                initialSort={initialSort}
                columnLabels={getColumns()}
                getUnauthorizedMessage={(serviceCase) => (
                  <UnauthorizedMessage
                    clickedDataObj={serviceCase}
                    currentProviderId={groupId}
                    isCaseManager={isCaseManager}
                  />
                )}
                getRowProps={(serviceCase) => (
                  isCasePendingAuth(serviceCase) ? {
                    clickable: false,
                    hoverable: false,
                  } : null
                )}
                rowIcon={(serviceCase) => {
                  const icon = (
                    <CaseRowIcon
                      isCC={isCC}
                      isCaseMgmtProvider={isCaseMgmtProvider}
                      serviceCase={serviceCase}
                    />
                  );

                  if (!isCasePendingAuth(serviceCase)) {
                    return icon;
                  }

                  return (
                    <Popover
                      id={`pending-auth-open-cases-${serviceCase.id}`}
                      content={PENDING_AUTHORIZATION_CASE_ACCESS_MESSAGE}
                      simpleMode
                      isVisible={pendingAuthPopoverCaseId === serviceCase.id}
                    >
                      {icon}
                    </Popover>
                  );
                }}
                emptyMessage="There are no open cases."
              />
            )
        }
      </div>
    </div>
  );
}

OpenCasesIndex.propTypes = {
  isFetching: PropTypes.bool.isRequired,
  data: PropTypes.array.isRequired,
  paging: shapes.paging,
  onNextClick: PropTypes.func.isRequired,
  onPrevClick: PropTypes.func.isRequired,
  onCellClick: PropTypes.func.isRequired,
  initialSort: PropTypes.object,
  groupId: PropTypes.string.isRequired,
  isCC: PropTypes.bool.isRequired,
  filters: PropTypes.array,
  onFiltersChange: PropTypes.func,
  isCaseMgmtProvider: PropTypes.bool,
  fetchGroupsUsers: PropTypes.func.isRequired,
  showDescription: PropTypes.bool,
  sortConfig: PropTypes.func,
  setSort: PropTypes.object,
  isCaseManager: PropTypes.bool,
};

function mapStateToProps(state) {
  return {
    showDescription: showFacesheetCasesTableDescription(state),
  };
}

export default connect(mapStateToProps)(OpenCasesIndex);
